import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── Check-ins ────────────────────────────────────────────────────────────────

export interface CheckInData {
  mood: number;
  urge: number;
  triggers: string[];
  diaryText: string;
}

export async function saveCheckIn(data: CheckInData) {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase.from('check_ins').insert({
    user_id: user.id,
    mood: data.mood,
    urge: data.urge,
    triggers: data.triggers,
    diary_text: data.diaryText,
  });

  if (error) {
    if (isSchemaError(error)) throw error;
    console.log('Check-in save error:', error.message);
    return null;
  }

  // Update streak
  await supabase.rpc || await supabase
    .from('user_profiles')
    .update({ streak_days: user.id })
    .eq('id', user.id);

  return true;
}

export async function getRecentCheckIns(limit = 7) {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('check_ins')
    .select('*')
    .eq('user_id', user.id)
    .order('checked_in_at', { ascending: false })
    .limit(limit);

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }

  return (data || []).map((row) => ({
    id: row.id,
    mood: row.mood,
    urge: row.urge,
    triggers: row.triggers || [],
    diaryText: row.diary_text,
    checkedInAt: row.checked_in_at,
    createdAt: row.created_at,
  }));
}

// ─── Exercise Completions ─────────────────────────────────────────────────────

export interface ExerciseCompletionData {
  exerciseId: string;
  exerciseName: string;
  category: string;
  durationMinutes?: number;
  rating?: number;
  moodBefore?: number;
  urgeBefore?: number;
}

export async function saveExerciseCompletion(data: ExerciseCompletionData) {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase.from('exercise_completions').insert({
    user_id: user.id,
    exercise_id: data.exerciseId,
    exercise_name: data.exerciseName,
    category: data.category,
    duration_minutes: data.durationMinutes || 0,
    rating: data.rating || null,
    mood_before: data.moodBefore || null,
    urge_before: data.urgeBefore || null,
  });

  if (error) {
    if (isSchemaError(error)) throw error;
    console.log('Exercise completion save error:', error.message);
    return null;
  }

  // Increment exercises_done counter
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('exercises_done')
    .eq('id', user.id)
    .single();

  if (profile) {
    await supabase
      .from('user_profiles')
      .update({ exercises_done: (profile.exercises_done || 0) + 1 })
      .eq('id', user.id);
  }

  return true;
}

export async function getExerciseHistory(limit = 20) {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('exercise_completions')
    .select('*')
    .eq('user_id', user.id)
    .order('completed_at', { ascending: false })
    .limit(limit);

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }

  return (data || []).map((row) => ({
    id: row.id,
    exerciseId: row.exercise_id,
    exerciseName: row.exercise_name,
    category: row.category,
    durationMinutes: row.duration_minutes,
    rating: row.rating,
    moodBefore: row.mood_before,
    urgeBefore: row.urge_before,
    completedAt: row.completed_at,
  }));
}

// ─── User Profile ─────────────────────────────────────────────────────────────

export async function getUserProfile() {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    if (isSchemaError(error)) throw error;
    return null;
  }

  return {
    id: data.id,
    email: data.email,
    displayName: data.display_name,
    avatarUrl: data.avatar_url,
    streakDays: data.streak_days || 0,
    urgeFreedays: data.urge_free_days || 0,
    exercisesDone: data.exercises_done || 0,
    notificationTime: data.notification_time || '09:00',
    notifyStreak: data.notify_streak ?? true,
    notifyWeekly: data.notify_weekly ?? true,
    notifyNudges: data.notify_nudges ?? true,
    notifyExercises: data.notify_exercises ?? false,
    notifyHighUrge: data.notify_high_urge ?? true,
    prefDarkMode: data.pref_dark_mode ?? false,
    prefAnonymous: data.pref_anonymous ?? false,
    prefShareOperator: data.pref_share_operator ?? false,
    language: data.language || 'en',
    createdAt: data.created_at,
  };
}

export async function updateUserProfile(updates: Partial<{
  displayName: string;
  notificationTime: string;
  notifyStreak: boolean;
  notifyWeekly: boolean;
  notifyNudges: boolean;
  notifyExercises: boolean;
  notifyHighUrge: boolean;
  prefDarkMode: boolean;
  prefAnonymous: boolean;
  prefShareOperator: boolean;
  language: string;
}>) {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const dbUpdates: Record<string, any> = {};
  if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
  if (updates.notificationTime !== undefined) dbUpdates.notification_time = updates.notificationTime;
  if (updates.notifyStreak !== undefined) dbUpdates.notify_streak = updates.notifyStreak;
  if (updates.notifyWeekly !== undefined) dbUpdates.notify_weekly = updates.notifyWeekly;
  if (updates.notifyNudges !== undefined) dbUpdates.notify_nudges = updates.notifyNudges;
  if (updates.notifyExercises !== undefined) dbUpdates.notify_exercises = updates.notifyExercises;
  if (updates.notifyHighUrge !== undefined) dbUpdates.notify_high_urge = updates.notifyHighUrge;
  if (updates.prefDarkMode !== undefined) dbUpdates.pref_dark_mode = updates.prefDarkMode;
  if (updates.prefAnonymous !== undefined) dbUpdates.pref_anonymous = updates.prefAnonymous;
  if (updates.prefShareOperator !== undefined) dbUpdates.pref_share_operator = updates.prefShareOperator;
  if (updates.language !== undefined) dbUpdates.language = updates.language;

  const { error } = await supabase
    .from('user_profiles')
    .update(dbUpdates)
    .eq('id', user.id);

  if (error) {
    if (isSchemaError(error)) throw error;
    console.log('Profile update error:', error.message);
    return null;
  }

  return true;
}
